// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { DateValidationError } from '@mui/x-date-pickers';
import { clientLogger } from '@seedplanner/logger';
import {
  MAX_ORDER_WEEKLY_FREQUENCY,
  dayOfWeekToNumber,
  formatTime,
  getRecurringOrderTimesToCreate,
  numberToDayOfWeek,
  utils,
} from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '../../lib/base';
import { Help } from '../../lib/help';
import { DASHBOARD_OVERDUE_TASKS_QUERY, DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import { RECURRING_ORDERS_QUERY } from '../../queries/RecurringOrderQueries';
import { SURPLUSES_BUFFERS_QUERY } from '../../queries/SurplusesQueries';
import type {
  EditRecurringOrderTimesMutation,
  EditRecurringOrderTimesMutationVariables,
  RecurringOrderEditTimesRecurringOrderDataQuery,
  RecurringOrderEditTimesRecurringOrderDataQueryVariables,
} from '../../types/__generated__/graphql';
import { createID, formatTimeToSkip, getTimeRanges } from '../../utils';
import { evictCacheData } from '../../utils/evictCacheData';
import { ExitDialogButton } from '../common/button';
import InternalDatePicker from '../common/date/InternalDatePicker';
import { ErrorView } from '../common/error';
import {
  DEFAULT_FREQUENCY_UNIT,
  EVERY_NTH_WEEKDAY_OF_MONTH,
  EVERY_X_DAY_OF_WEEK,
  EVERY_X_WEEKS,
  type FrequencyLabel,
  daysOfTheWeek,
  frequencyLabelToInput,
  orderFrequencyOptions,
  setFrequencyStates,
} from '../common/frequency';
import { PageLoading } from '../common/loading';
import { prepareFrequencyForSubmit } from '../order/orderFormUtils';
import CancellableOrdersList from './CancellableOrdersList';
import { ChildOrdersContainer } from './ChildOrdersContainer';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const EDIT_RECURRING_ORDER_TIMES_MUT = gql`
  mutation EditRecurringOrderTimes(
    $recurring_order_id: Int!
    $recurring_start_time: Int!
    $recurring_end_time: Int
    $frequency_options: RecurringOrderOptionsInput!
    $should_create_overdue_orders: Boolean!
    $in_progress_orders_to_cancel: [Int!]!
    $overdue_orders_to_create: [String!]!
  ) {
    editRecurringOrderTimes(
      input: {
        recurring_order_id: $recurring_order_id
        recurring_start_time: $recurring_start_time
        recurring_end_time: $recurring_end_time
        frequency_options: $frequency_options
        should_create_overdue_orders: $should_create_overdue_orders
        in_progress_orders_to_cancel: $in_progress_orders_to_cancel
        overdue_orders_to_create: $overdue_orders_to_create
      }
    ) {
      updated_recurring_order {
        id
        recurring_options {
          exceptions
          frequencyGroups {
            frequencyType
            frequencies
          }
        }
        recurring_start_time
        recurring_end_time
        updated_at
      }
      deleted_orders {
        id
      }
      deleted_inputs {
        id
      }
      deleted_plantings {
        id
      }
      deleted_tasks {
        id
      }
      plantings_created {
        id
      }
      tasks_created {
        id
      }
      orders_created {
        id
      }
    }
  }
`;
const RECURRING_ORDER_TIMES_QUERY = gql`
  query RecurringOrderEditTimesRecurringOrderData($id: ID!) {
    recurring_order(id: $id) {
      id
      recurring_start_time
      recurring_end_time
      recurring_options {
        exceptions
        frequencyGroups {
          frequencyType
          frequencies
        }
      }
      inputs {
        edges {
          node {
            id
            crop {
              id
              total_grow_time
              harvest_buffer_time
            }
            blend {
              id
              crops {
                edges {
                  node {
                    id
                    crop {
                      id
                      total_grow_time
                      harvest_buffer_time
                    }
                  }
                }
              }
            }
          }
        }
      }
      inProgressOrders: child_orders(
        filters: [
          { name: "order.order_status", operator: "=", value: "in_progress" }
        ]
        orderBy: [
          { name: "order.order_fulfillment_time", direction: "asc" }
        ]
      ) {
        edges {
          node {
            id
            order_fulfillment_time
            order_status
            plantings {
              edges {
                node {
                  id
                  planting_status
                  trays_rounded
                  yield_per_tray_ounces
                  crop {
                    id
                    crop_name
                    yield_unit
                  }
                }
              }
            }
          }
        }
      }
      plannedOrders: child_orders(
        filters: [
          { name: "order.order_status", operator: "=", value: "planned" }
        ]
        orderBy: [
          { name: "order.order_fulfillment_time", direction: "asc" }
        ]
      ) {
        edges {
          node {
            id
            order_fulfillment_time
            order_status
          }
        }
      }
      cancelledOrders: child_orders(
        filters: [
          { name: "order.order_status", operator: "=", value: "cancelled" }
        ]
        orderBy: [
          { name: "order.order_fulfillment_time", direction: "asc" }
        ]
      ) {
        edges {
          node {
            id
            order_fulfillment_time
            order_status
          }
        }
      }
    }
  }
`;

interface Props {
  readonly recurringOrderId: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: (shouldRefetchSelf: boolean) => void;
}

export default function EditRecurringOrderTimesDialog({ recurringOrderId, isOpen, onClose, onSuccess }: Props) {
  const { timezone } = useUserContext();
  const apolloClient = useApolloClient();
  const [editRecurringOrderTimes, { loading: editRecurringOrderLoading }] = useMutation<
    EditRecurringOrderTimesMutation,
    EditRecurringOrderTimesMutationVariables
  >(EDIT_RECURRING_ORDER_TIMES_MUT);
  const [
    getRecurringOrderData,
    { data: recurringOrderData, loading: recurringOrderDataLoading, error: recurringOrderDataError },
  ] = useLazyQuery<
    RecurringOrderEditTimesRecurringOrderDataQuery,
    RecurringOrderEditTimesRecurringOrderDataQueryVariables
  >(RECURRING_ORDER_TIMES_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      void getRecurringOrderData({
        variables: { id: createID.recurringOrder(recurringOrderId) },
        onCompleted: (orderDataIn) => {
          if (orderDataIn?.recurring_order != null) {
            setExistingExceptions(orderDataIn.recurring_order.recurring_options.exceptions);

            setFrequencyStates({
              timezone,
              recurringOptions: orderDataIn.recurring_order.recurring_options,
              startTime: orderDataIn.recurring_order.recurring_start_time,
              endTime: orderDataIn.recurring_order.recurring_end_time,
              setFrequencyUnit,
              setFrequency,
              setDayOfWeek,
              setOccurence,
              setRecursForever,
              setStartDate,
              setEndDate,
              setExistingFrequencyUnit,
              setExistingFrequency,
              setExistingDayOfWeek,
              setExistingOccurence,
              setExistingRecursForever,
              setExistingStartDate,
              setExistingEndDate,
            });
          }
        },
      });
    }
  }, [isOpen, getRecurringOrderData, recurringOrderId, timezone]);

  const disableFields = editRecurringOrderLoading;

  const [startDate, setStartDate] = React.useState<dayjs.Dayjs | null>(utils.todayStart());
  const [endDate, setEndDate] = React.useState<dayjs.Dayjs | null>(null);
  const [recursForever, setRecursForever] = React.useState(true);
  const [shouldCreateOverdueOrders, setShouldCreateOverdueOrders] = React.useState(false);
  const [startDateError, setStartDateError] = React.useState<DateValidationError | null>(null);
  // Frequency for every X weeks
  const [frequency, setFrequency] = React.useState('1');
  // Frequency type: every X weeks, every X day of the week, every nth weekday of the month
  const [frequencyUnit, setFrequencyUnit] = React.useState(DEFAULT_FREQUENCY_UNIT);
  // Day of the week for every nth weekday of the month or for every X day of the week
  const [dayOfWeek, setDayOfWeek] = React.useState(numberToDayOfWeek(dayjs().day()));
  // Occurence for every nth weekday of the month
  const [occurence, setOccurence] = React.useState('1');

  const [existingStartDate, setExistingStartDate] = React.useState<dayjs.Dayjs | null>(null);
  const [existingEndDate, setExistingEndDate] = React.useState<dayjs.Dayjs | null>(null);
  const [existingFrequency, setExistingFrequency] = React.useState('');
  const [existingFrequencyUnit, setExistingFrequencyUnit] = React.useState(DEFAULT_FREQUENCY_UNIT);
  const [existingDayOfWeek, setExistingDayOfWeek] = React.useState('');
  const [existingRecursForever, setExistingRecursForever] = React.useState(true);
  const [existingExceptions, setExistingExceptions] = React.useState<string[]>([]);
  const [existingOccurence, setExistingOccurence] = React.useState('1');

  const [selectedCancellableOrders, setSelectedCancellableOrders] = React.useState<number[]>([]);
  const onSetSelectedCancellableOrders = (orders: number[]) => {
    setSelectedCancellableOrders(orders);
  };

  const startDateErrorMessage = React.useMemo(() => {
    if (startDateError == null) {
      return '';
    }
    switch (startDateError) {
      case 'maxDate':
        return 'Start date cannot be after end date';
      case 'minDate':
        return 'Start date cannot be before today';
      default:
        return '';
    }
  }, [startDateError]);

  const { todayStart, todayStartDate, sevenDaysFromTodayStart } = getTimeRanges(timezone);

  const onFrequencyUnitChange = (e: SelectChangeEvent<string>) => {
    setFrequencyUnit(e.target.value);
  };
  const onDayOfWeekChange = (e: SelectChangeEvent<string>) => {
    setDayOfWeek(e.target.value);
  };
  const onChangeFrequencySelect = (e: SelectChangeEvent<string>) => {
    setFrequency(e.target.value);
  };
  const onChangeOccurence = (e: SelectChangeEvent<string>) => {
    setOccurence(e.target.value);
  };
  const onChangeEndDate = (newValue: dayjs.Dayjs | null) => {
    setEndDate(newValue);
  };
  const onChangeRecursForever = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRecursForever(e.target.checked);
  };
  const onChangeStartDate = (newValue: dayjs.Dayjs | null) => {
    setStartDate(newValue);
  };
  const onChangeShouldCreateOverdueOrders = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldCreateOverdueOrders(e.target.checked);
  };

  const onSubmit = () => {
    const recurringOrderInput = prepareFrequencyForSubmit({
      frequencyUnit,
      frequency,
      dayOfWeek,
      occurence,
      recursForever,
      startDate: startDate,
      endDate,
      timezone,
    });
    void editRecurringOrderTimes({
      refetchQueries: [
        {
          query: DASHBOARD_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
          },
        },
        {
          query: DASHBOARD_OVERDUE_TASKS_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            first: 25,
          },
        },
        { query: SURPLUSES_BUFFERS_QUERY, variables: { timeAfter: todayStart } },
        { query: RECURRING_ORDERS_QUERY, variables: { todayStart: todayStart.toString() } },
      ],
      variables: {
        recurring_order_id: recurringOrderId,
        recurring_start_time: recurringOrderInput.start_date,
        recurring_end_time: recurringOrderInput.end_date,
        frequency_options: recurringOrderInput.options,
        should_create_overdue_orders: shouldCreateOverdueOrders,
        in_progress_orders_to_cancel: selectedCancellableOrders,
        overdue_orders_to_create: [],
      },
      update: (cache, { data }) => {
        const plantingsCreated = (data?.editRecurringOrderTimes?.plantings_created?.length ?? 0) > 0;
        const plantingsDeleted = (data?.editRecurringOrderTimes?.deleted_plantings?.length ?? 0) > 0;
        const tasksCreated = (data?.editRecurringOrderTimes?.tasks_created?.length ?? 0) > 0;
        const tasksDeleted = (data?.editRecurringOrderTimes?.deleted_tasks?.length ?? 0) > 0;
        const ordersCreated = (data?.editRecurringOrderTimes?.orders_created?.length ?? 0) > 0;
        const ordersDeleted = (data?.editRecurringOrderTimes?.deleted_orders?.length ?? 0) > 0;
        cache.modify({
          fields: {
            harvests(existingGroup, { DELETE }) {
              return plantingsCreated || plantingsDeleted ? DELETE : existingGroup;
            },
            plantings(existingGroup, { DELETE }) {
              return plantingsCreated || plantingsDeleted ? DELETE : existingGroup;
            },
            task_groups(existingGroup, { DELETE }) {
              return tasksCreated || tasksDeleted ? DELETE : existingGroup;
            },
            tasks(existingTasks, { DELETE }) {
              return tasksCreated || tasksDeleted ? DELETE : existingTasks;
            },
            taskGroup(existingGroup, { DELETE }) {
              return tasksCreated ? DELETE : existingGroup;
            },
            plantingGroup(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
            planting_groups(existingGroup, { DELETE }) {
              return plantingsCreated || plantingsDeleted ? DELETE : existingGroup;
            },
            plantingTimeGroup(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
            orders(existingOrders, { DELETE }) {
              return ordersCreated || ordersDeleted ? DELETE : existingOrders;
            },
          },
        });
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editRecurringOrderTimes != null) {
          enqueueSnackbar('Updated recurring order times', { variant: 'success' });
          setStartDate(utils.todayStart());
          setEndDate(null);
          setFrequencyUnit(DEFAULT_FREQUENCY_UNIT);
          setRecursForever(true);
          setFrequency('');
          setOccurence('1');
          setDayOfWeek('');
          setShouldCreateOverdueOrders(false);
          evictCacheData({
            apolloClient,
            orderIds: dataIn.editRecurringOrderTimes.deleted_orders.map((o) => o.id),
            orderInputIds: dataIn.editRecurringOrderTimes.deleted_inputs.map((i) => i.id),
            taskIds: dataIn.editRecurringOrderTimes.deleted_tasks.map((t) => t.id),
            plantingIds: dataIn.editRecurringOrderTimes.deleted_plantings.map((p) => p.id),
          });
          onSuccess(true);
        } else {
          enqueueSnackbar('Error editing recurring order', { variant: 'error', autoHideDuration: 10000 });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, {
          variant: 'error',
          autoHideDuration: 10000,
        });
      },
    });
  };

  const hasInputValidationError = startDateErrorMessage !== '';
  const requiredFieldsFilled: boolean =
    startDate !== null &&
    (frequencyUnit === EVERY_X_DAY_OF_WEEK ? dayOfWeek !== '' : frequency !== '') &&
    (recursForever ? true : endDate !== null);

  const inputs = recurringOrderData?.recurring_order?.inputs?.edges.map((n) => n.node) ?? [];
  const cropGrowTimes = inputs.map((n) => {
    if (n?.crop == null) {
      return Number.NEGATIVE_INFINITY;
    }

    return n.crop.total_grow_time + n.crop.harvest_buffer_time;
  });
  const blends = inputs.map((n) => n.blend);
  const blendCropGrowTimes = blends.flatMap((n) => {
    return (
      n?.crops.edges.map((m) => {
        if (m?.node.crop == null) {
          return Number.NEGATIVE_INFINITY;
        }

        return m.node.crop.total_grow_time + m.node.crop.harvest_buffer_time;
      }) ?? []
    );
  });
  const longestTotalGrowTimePlusBuffer = Math.max(...cropGrowTimes, ...blendCropGrowTimes);

  const startDateChanged = startDate?.format() !== existingStartDate?.format();
  const endDateChanged = !recursForever && endDate?.format() !== existingEndDate?.format();
  const frequencyUnitChanged = frequencyUnit !== existingFrequencyUnit;
  const dayOfWeekChanged =
    (frequencyUnit === EVERY_X_DAY_OF_WEEK || frequencyUnit === EVERY_NTH_WEEKDAY_OF_MONTH) &&
    dayOfWeek !== existingDayOfWeek;
  const frequencyChanged = frequencyUnit === EVERY_X_WEEKS && frequency !== existingFrequency;
  const recursForeverChanged = recursForever !== existingRecursForever;
  const occurenceChanged = frequencyUnit === EVERY_NTH_WEEKDAY_OF_MONTH && occurence !== existingOccurence;

  const somethingChanged =
    startDateChanged ||
    endDateChanged ||
    frequencyChanged ||
    frequencyUnitChanged ||
    dayOfWeekChanged ||
    recursForeverChanged ||
    occurenceChanged ||
    shouldCreateOverdueOrders;

  const isSubmitDisabled: boolean =
    !requiredFieldsFilled || hasInputValidationError || disableFields || !somethingChanged;
  // const onEnter =
  //   (_event: React.KeyboardEvent<HTMLInputElement>) => {
  //     if (!isSubmitDisabled) {
  //       onSubmit();
  //     }
  //   };

  const inProgressChildOrders = recurringOrderData?.recurring_order?.inProgressOrders?.edges.map((n) => n.node) ?? [];
  const plannedChildOrders = recurringOrderData?.recurring_order?.plannedOrders?.edges.map((n) => n.node) ?? [];
  const cancelledChildOrders = recurringOrderData?.recurring_order?.cancelledOrders?.edges.map((n) => n.node) ?? [];

  const existingOrderTimes = new Set(
    [
      ...inProgressChildOrders.map((o) => o.order_fulfillment_time),
      ...cancelledChildOrders.map((o) => o.order_fulfillment_time),
      // ...plannedChildOrders.map((o) => o.order_fulfillment_time),
    ].map((time) => formatTimeToSkip(time, timezone)),
  );

  const newOrderTimes = getRecurringOrderTimesToCreate({
    recurringStartTime: startDate?.unix() || utils.todayStart(timezone).unix(),
    recurringEndTime: recursForever ? undefined : endDate?.unix() || undefined, // endDate?.unix() can be NaN
    timezone,
    recurringOptions: {
      exceptions: existingExceptions,
      frequencyGroups: [
        {
          frequencyType: frequencyLabelToInput(frequencyUnit as FrequencyLabel),
          frequencies:
            frequencyUnit === EVERY_NTH_WEEKDAY_OF_MONTH
              ? [dayOfWeekToNumber(dayOfWeek || 'mon'), Number.parseInt(occurence || '1')]
              : [
                  frequencyUnit === EVERY_X_DAY_OF_WEEK
                    ? dayOfWeekToNumber(dayOfWeek || 'mon')
                    : Number.parseInt(frequency || '1'),
                ],
        },
      ],
    },
    longestTotalGrowTimePlusBuffer,
    ignoreGrowTimeLimit: true,
  })
    .map((time) => ({
      time,
      isOverdue: time - longestTotalGrowTimePlusBuffer < todayStart,
      formattedTime: formatTime(time, { timezone }),
      formattedTimeToSkip: formatTimeToSkip(time, timezone),
    }))
    .filter((time) => {
      const shouldSkip = existingOrderTimes.has(time.formattedTimeToSkip);
      if (shouldSkip) {
        clientLogger.info({ title: 'edit_recurring_order_time_skip', date: time.formattedTimeToSkip });
      }

      return !shouldSkip;
    });
  const overdueTimes = newOrderTimes.filter((time) => time.isOverdue);
  const futureTimes = newOrderTimes.filter((time) => !time.isOverdue);

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" gap={2}>
        <Box display="flex" flexDirection="column" sx={{ pl: '14px' }}>
          <FormControl required={true}>
            <FormLabel id="order-recurs-forever-label">Recur Forever</FormLabel>
            <Switch checked={recursForever} onChange={onChangeRecursForever} />
          </FormControl>
        </Box>
      </Box>
      <InternalDatePicker
        label="Start Date *"
        value={startDate}
        onChange={onChangeStartDate}
        disabled={disableFields}
        minDate={startDateChanged ? todayStartDate : (existingStartDate ?? undefined)}
        maxDate={!recursForever ? (endDate ?? undefined) : undefined}
        onError={(newError) => setStartDateError(newError)}
        textFieldProps={{
          helperText: startDateErrorMessage,
        }}
      />
      {!recursForever && (
        <InternalDatePicker
          minDate={startDate ?? todayStartDate}
          label="End Date *"
          value={endDate}
          onChange={onChangeEndDate}
          disabled={disableFields}
        />
      )}
      <Box>
        <FormControl fullWidth>
          <InputLabel id="frequency-unit-selector">Frequency Type</InputLabel>
          <Select
            aria-describedby="frequency-unit-selector"
            id="frequency-unit-selector"
            labelId="frequency-unit-selector"
            label="Frequency Type"
            value={frequencyUnit}
            onChange={onFrequencyUnitChange}
            // onEnter={onEnter}
            required={true}
            disabled={disableFields}
          >
            {orderFrequencyOptions.map((option) => {
              return (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
      <Box>
        {frequencyUnit === EVERY_X_DAY_OF_WEEK ? (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
            <Typography>Every</Typography>
            <FormControl fullWidth>
              <InputLabel id="frequency-day-of-week-selector">Day of Week</InputLabel>
              <Select
                aria-describedby="frequency-day-of-week-selector"
                id="frequency-day-of-week-selector"
                labelId="frequency-day-of-week-selector"
                label="Day of Week"
                value={dayOfWeek}
                onChange={onDayOfWeekChange}
                // onEnter={onEnter}
                required={true}
                disabled={disableFields}
              >
                {daysOfTheWeek.map((day) => {
                  return (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        ) : frequencyUnit === EVERY_NTH_WEEKDAY_OF_MONTH ? (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
            <Typography>Every</Typography>
            <FormControl fullWidth>
              <InputLabel id="frequency-nth-week-of-month-selector">Occurrence</InputLabel>
              <Select
                aria-describedby="frequency-nth-week-of-month-selector"
                id="frequency-nth-week-of-month-selector"
                labelId="frequency-nth-week-of-month-selector"
                label="Occurence"
                value={occurence}
                onChange={onChangeOccurence}
                // onEnter={onEnter}
                required={true}
                disabled={disableFields}
              >
                {[
                  { value: '0', label: '1st' },
                  { value: '1', label: '2nd' },
                  { value: '2', label: '3rd' },
                  { value: '3', label: '4th' },
                ].map((occ) => {
                  return (
                    <MenuItem key={occ.value} value={occ.value}>
                      {occ.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="frequency-day-of-week-selector">Day of Week</InputLabel>
              <Select
                aria-describedby="frequency-day-of-week-selector"
                id="frequency-day-of-week-selector"
                labelId="frequency-day-of-week-selector"
                label="Day of Week"
                value={dayOfWeek}
                onChange={onDayOfWeekChange}
                // onEnter={onEnter}
                required={true}
                disabled={disableFields}
              >
                {daysOfTheWeek.map((day) => {
                  return (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Typography>of the month</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
            <Typography>Every</Typography>
            <FormControl fullWidth>
              <InputLabel id="weekly-recurring-order-frequency">Frequency</InputLabel>
              <Select
                aria-describedby="weekly-recurring-order-frequency"
                id="weekly-recurring-order-frequency"
                labelId="weekly-recurring-order-frequency"
                label="Frequency"
                value={frequency}
                onChange={onChangeFrequencySelect}
                // onEnter={onEnter}
                required={true}
                disabled={disableFields}
              >
                {Array.from({ length: MAX_ORDER_WEEKLY_FREQUENCY }, (_, i) => (i + 1).toString()).map((freq) => {
                  return (
                    <MenuItem key={freq} value={freq}>
                      {freq}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Typography>weeks</Typography>
          </Box>
        )}
      </Box>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" justifyContent="flex-start">
        <FormControlLabel
          label="Create Overdue Orders"
          control={<Checkbox checked={shouldCreateOverdueOrders} onChange={onChangeShouldCreateOverdueOrders} />}
        />
        <Help tooltip="By checking this, Microgreen Manager will create orders that are too late to plant, given the delivery date and the grow time of the crops needed. This is useful for when you are first getting setup with Microgreen Manager. See the Tutorials page for a more detailed explanation." />
      </Box>
      <Box>
        {inProgressChildOrders.length > 0 && (
          <CancellableOrdersList
            selectedOrders={selectedCancellableOrders}
            setSelectedOrders={onSetSelectedCancellableOrders}
            orders={inProgressChildOrders}
            timezone={timezone}
          />
        )}
        {plannedChildOrders.length > 0 && (
          <ChildOrdersContainer
            title="The following planned child orders will be deleted:"
            ordersIn={plannedChildOrders}
          />
        )}
        <Collapse in={shouldCreateOverdueOrders} timeout="auto" unmountOnExit>
          <Typography>Overdue orders will be created for these dates:</Typography>
          {overdueTimes.length > 0 ? (
            <ul>
              {overdueTimes.map(({ time }) => {
                return <li key={time}>{formatTime(time, { timezone })}</li>;
              })}
            </ul>
          ) : (
            <ul>
              <li>No overdue orders will be created.</li>
            </ul>
          )}
        </Collapse>
        <Typography>New orders will be created for these dates:</Typography>
        {futureTimes.length > 0 ? (
          <ul>
            {futureTimes.map(({ time, formattedTime }) => {
              return <li key={time}>{formattedTime}</li>;
            })}
          </ul>
        ) : (
          <ul>
            <li>No new upcoming orders will be created now.</li>
          </ul>
        )}
      </Box>
    </Box>
  );

  let dialogContent = form;
  dialogContent = recurringOrderDataError ? (
    <ErrorView error={recurringOrderDataError} />
  ) : recurringOrderDataLoading || recurringOrderData?.recurring_order == null ? (
    <PageLoading />
  ) : (
    form
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Recurring Order Times</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{dialogContent}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button sx={{ width: { xs: '100%', md: '50%' } }} variant="outlined" onClick={onClose} disabled={disableFields}>
          Cancel
        </Button>
        <Button
          id="submit-order-button"
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
